import type { TranslationKey } from '@dreamgame/shared';
import { signal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { tv } from 'tailwind-variants';
import { pawnsVersion } from '../../state/entities';
import { t } from '../../state/i18n';
import { selectedPawns } from '../../state/selection';
import { CombatPanel } from './CombatPanel';
import { EquipmentPanel } from './EquipmentPanel';
import { InventoryPanel } from './InventoryPanel';
import { NeedsPanel } from './NeedsPanel';
import { StatsPanel } from './StatsPanel';

// Panel tabs
export const PanelTab = {
  NEEDS: 'needs',
  COMBAT: 'combat',
  STATS: 'stats',
  EQUIPMENT: 'equipment',
  INVENTORY: 'inventory',
} as const;

export type PanelTabType = (typeof PanelTab)[keyof typeof PanelTab];

interface TabConfig {
  readonly id: PanelTabType;
  readonly label: TranslationKey;
  readonly icon: string;
}

const TABS: readonly TabConfig[] = [
  { id: PanelTab.NEEDS, label: 'ui.tabs.needs', icon: 'heart' },
  { id: PanelTab.STATS, label: 'ui.tabs.stats', icon: 'chart' },
  { id: PanelTab.EQUIPMENT, label: 'ui.tabs.worn', icon: 'shirt' },
  { id: PanelTab.INVENTORY, label: 'ui.tabs.inv', icon: 'backpack' },
] as const;

// State
export const activeTab = signal<PanelTabType>(PanelTab.NEEDS);

// Styles
const gamePanel = tv({
  slots: {
    root: [
      'fixed right-[1.1em] top-1/2 -translate-y-1/2',
      'w-[18.8em]',
      'animate-slide-left',
      'z-[1000]',
      'text-[1rem] md:text-[1.0625rem] xl:text-[1.125rem]',
      'subpixel-antialiased',
    ],
    container: [
      'bg-gradient-to-b from-rs-stone to-rs-stone-dark',
      'border-2 border-rs-stone-light',
      'rounded-lg',
      'shadow-rs-panel',
      'overflow-hidden',
    ],
    tabBar: [
      'flex',
      'bg-gradient-to-b from-rs-stone-light to-rs-stone',
      'border-b-2 border-rs-stone-dark',
    ],
    tab: [
      'flex-1',
      'py-[0.6em] px-[0.3em]',
      'text-[0.62em] uppercase tracking-wide font-semibold',
      'text-rs-parchment-dark',
      'border-r border-rs-stone-dark last:border-r-0',
      'transition-all duration-150',
      'hover:bg-rs-stone-dark/30',
      'flex flex-col items-center gap-[0.2em]',
      'cursor-pointer',
    ],
    tabActive: ['bg-rs-stone-dark', 'text-rs-gold', 'shadow-rs-inset'],
    tabIcon: ['w-[1.45em] h-[1.45em]', 'flex items-center justify-center'],
    content: ['p-[0.95em]', 'min-h-[20em]'],
    title: [
      'text-center text-rs-gold font-semibold text-[0.88em] mb-[0.8em] tracking-wide',
      'border-b border-rs-stone-light/30 pb-[0.55em]',
    ],
    emptyState: [
      'flex flex-col items-center justify-center',
      'h-[17.5em]',
      'text-rs-parchment-dark text-[0.72em] text-center',
    ],
  },
});

// Tab icons as simple SVG paths
const TabIcon = ({ type }: { type: string }) => {
  const iconPaths: Record<string, ComponentChildren> = {
    heart: (
      <svg viewBox="0 0 24 24" fill="currentColor" class="w-[1.05em] h-[1.05em]" aria-hidden="true">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      </svg>
    ),
    swords: (
      <svg viewBox="0 0 24 24" fill="currentColor" class="w-[1.05em] h-[1.05em]" aria-hidden="true">
        <path d="M6.92 5L5 7l4.5 4.5L7 14l1.5 1.5 2.5-2.5 4.5 4.5 2-1.92L5 3zm10.16 0L20 8l-3 3 3 3-2.08 2.08L13.5 11.5 11 14l-1.5-1.5 2.5-2.5-4.5-4.5L9.58 3 17.08 5z" />
      </svg>
    ),
    chart: (
      <svg viewBox="0 0 24 24" fill="currentColor" class="w-[1.05em] h-[1.05em]" aria-hidden="true">
        <path d="M3 3v18h18V3H3zm16 16H5V5h14v14zM7 10h2v7H7v-7zm4-3h2v10h-2V7zm4 6h2v4h-2v-4z" />
      </svg>
    ),
    shirt: (
      <svg viewBox="0 0 24 24" fill="currentColor" class="w-[1.05em] h-[1.05em]" aria-hidden="true">
        <path d="M16 21H8a1 1 0 01-1-1V8l-4-5 3.5-1L12 6l5.5-4L21 3l-4 5v12a1 1 0 01-1 1z" />
      </svg>
    ),
    backpack: (
      <svg viewBox="0 0 24 24" fill="currentColor" class="w-[1.05em] h-[1.05em]" aria-hidden="true">
        <path d="M17 4H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2zm0 16H7V6h10v14zM9 2h6v2H9V2zm1 8h4v2h-4v-2z" />
      </svg>
    ),
  };

  return iconPaths[type] ?? null;
};

export const GamePanel = () => {
  // Subscribe to pawnsVersion to ensure re-renders when pawn data changes
  const _pawnVersion = pawnsVersion.value;
  const pawns = selectedPawns.value;
  const styles = gamePanel();

  // Only show when exactly one pawn is selected
  if (pawns.length !== 1) {
    return null;
  }

  const pawn = pawns[0];
  const currentTab = activeTab.value;

  const handleTabClick = ({ tab }: { tab: PanelTabType }) => {
    activeTab.value = tab;
  };

  const renderContent = () => {
    if (!pawn) {
      return null;
    }

    const pawnKey = `${pawn.networkId}`;

    switch (currentTab) {
      case PanelTab.NEEDS:
        return <NeedsPanel key={pawnKey} pawn={pawn} />;
      case PanelTab.COMBAT:
        return <CombatPanel key={pawnKey} pawn={pawn} />;
      case PanelTab.STATS:
        return <StatsPanel key={pawnKey} pawn={pawn} />;
      case PanelTab.EQUIPMENT:
        return <EquipmentPanel pawn={pawn} />;
      case PanelTab.INVENTORY:
        return <InventoryPanel pawn={pawn} />;
      default:
        return null;
    }
  };

  return (
    <div class={styles.root()}>
      <div class={styles.container()}>
        <div class={styles.tabBar()}>
          {TABS.map((tab) => (
            <button
              type="button"
              key={tab.id}
              class={`${styles.tab()} ${currentTab === tab.id ? styles.tabActive() : ''}`}
              onClick={() => handleTabClick({ tab: tab.id })}
              aria-label={tab.label}
            >
              <span class={styles.tabIcon()}>
                <TabIcon type={tab.icon} />
              </span>
              <span>{t(tab.label)}</span>
            </button>
          ))}
        </div>
        <div class={styles.content()}>{renderContent()}</div>
      </div>
    </div>
  );
};
